import { useEffect, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Breadcrumb } from '@/components/ui/Breadcrumb'
import { Spinner } from '@/components/ui/Spinner'
import { useProject, useUpdateProject, useDeleteProject } from '@/hooks/useProjects'
import { useClients } from '@/hooks/useClients'
import { useAdmins } from '@/hooks/useAdmins'
import { projectSchema, type ProjectFormData, PROJECT_STATUS_CONFIG } from '@/types/project'
import { ArrowLeft, FolderKanban, Trash2 } from 'lucide-react'

export default function EditProjectPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: project, isLoading: loadingProject } = useProject(id)
  const updateProject = useUpdateProject()
  const deleteProject = useDeleteProject()
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const { data: clientsData, isLoading: loadingClients } = useClients({ limit: 1000 })
  const { data: adminsData, isLoading: loadingAdmins } = useAdmins()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ProjectFormData>({
    resolver: zodResolver(projectSchema),
  })

  useEffect(() => {
    if (project) {
      reset({
        nom: project.nom,
        description: project.description || '',
        status: project.status,
        client_id: project.client_id,
        admin_id: project.admin_id || null,
        budget: project.budget ?? null,
        date_debut: project.date_debut || null,
        date_fin: project.date_fin || null,
      })
    }
  }, [project, reset])

  const onSubmit = async (data: ProjectFormData) => {
    if (!id) return
    try {
      await updateProject.mutateAsync({ id, data })
      navigate(`/projects/${id}`)
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : ''
      console.error('Erreur lors de la modification du projet:', errorMessage)
      alert('Erreur lors de la modification du projet. Veuillez reessayer.')
    }
  }

  const handleDelete = async () => {
    if (!id) return
    try {
      await deleteProject.mutateAsync(id)
      navigate('/projects')
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : ''
      console.error('Erreur lors de la suppression du projet:', errorMessage)
      alert('Erreur lors de la suppression du projet. Veuillez reessayer.')
    }
  }

  const isLoading = loadingProject || loadingClients || loadingAdmins

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          { label: 'Projets', href: '/projects' },
          { label: project?.nom || '...', href: `/projects/${id}` },
          { label: 'Modifier' },
        ]}
      />

      {/* Back button */}
      <Link to={`/projects/${id}`}>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<ArrowLeft className="w-4 h-4" />}
        >
          Retour
        </Button>
      </Link>

      <Card padding="lg">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-mint-100 flex items-center justify-center">
              <FolderKanban className="w-6 h-6 text-mint-600" />
            </div>
            <div>
              <CardTitle>Modifier le projet</CardTitle>
              <p className="text-sm text-brown-600 mt-1">
                Modifiez les informations du projet
              </p>
            </div>
          </div>
        </CardHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Project Name */}
            <Input
              label="Nom du projet *"
              {...register('nom')}
              error={errors.nom?.message}
              placeholder="Ex: Site vitrine pour restaurant"
            />

            {/* Description */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-brown-700">
                Description
              </label>
              <textarea
                {...register('description')}
                rows={4}
                className="w-full px-3 py-2 border border-beige-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mint-500 focus:border-transparent resize-none"
                placeholder="Decrivez les objectifs et details du projet..."
              />
              {errors.description && (
                <p className="text-sm text-red-600">{errors.description.message}</p>
              )}
            </div>

            {/* Client Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-brown-700">
                Client *
              </label>
              <Select
                {...register('client_id')}
                error={errors.client_id?.message}
              >
                <option value="">Selectionnez un client</option>
                {clientsData?.clients?.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.full_name} {client.company ? `(${client.company})` : ''}
                  </option>
                ))}
              </Select>
              {errors.client_id && (
                <p className="text-sm text-red-600">{errors.client_id.message}</p>
              )}
            </div>

            {/* Admin Assignment (optional) */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-brown-700">
                Admin assigne (optionnel)
              </label>
              <Select {...register('admin_id')}>
                <option value="">Aucun admin assigne</option>
                {adminsData?.map((admin) => (
                  <option key={admin.id} value={admin.id}>
                    {admin.full_name} ({admin.email})
                  </option>
                ))}
              </Select>
              {errors.admin_id && (
                <p className="text-sm text-red-600">{errors.admin_id.message}</p>
              )}
            </div>

            {/* Status */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-brown-700">
                Statut *
              </label>
              <Select {...register('status')} error={errors.status?.message}>
                {Object.entries(PROJECT_STATUS_CONFIG).map(([value, config]) => (
                  <option key={value} value={value}>
                    {config.label}
                  </option>
                ))}
              </Select>
            </div>

            {/* Budget */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-brown-700">
                Budget (€)
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                {...register('budget', {
                  setValueAs: (v) => v === '' || v === null ? null : parseFloat(v),
                })}
                error={errors.budget?.message}
                placeholder="Ex: 5000"
              />
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-brown-700">
                  Date de debut
                </label>
                <Input
                  type="date"
                  {...register('date_debut')}
                  error={errors.date_debut?.message}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-brown-700">
                  Date de fin prevue
                </label>
                <Input
                  type="date"
                  {...register('date_fin')}
                  error={errors.date_fin?.message}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-beige-200">
              <Link to={`/projects/${id}`}>
                <Button type="button" variant="outline">
                  Annuler
                </Button>
              </Link>
              <Button
                type="submit"
                disabled={isSubmitting}
                leftIcon={isSubmitting ? <Spinner size="sm" /> : <FolderKanban className="w-4 h-4" />}
              >
                {isSubmitting ? 'Enregistrement...' : 'Enregistrer les modifications'}
              </Button>
            </div>
          </form>
        )}
      </Card>

      {/* Zone de suppression */}
      {!isLoading && project && (
        <Card padding="lg" className="border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-red-800">Supprimer ce projet</h3>
              <p className="text-sm text-red-600 mt-1">
                Cette action est irreversible. Toutes les donnees associees seront supprimees.
              </p>
            </div>
            {showDeleteConfirm ? (
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Annuler
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={handleDelete}
                  disabled={deleteProject.isPending}
                  leftIcon={deleteProject.isPending ? <Spinner size="sm" /> : <Trash2 className="w-4 h-4" />}
                >
                  {deleteProject.isPending ? 'Suppression...' : 'Confirmer la suppression'}
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-red-300 text-red-700 hover:bg-red-50"
                onClick={() => setShowDeleteConfirm(true)}
                leftIcon={<Trash2 className="w-4 h-4" />}
              >
                Supprimer
              </Button>
            )}
          </div>
        </Card>
      )}
    </div>
  )
}
